import { z } from 'zod';
import { ADJUSTMENT_NAMES, REAL_FEED_NAMES } from './marketdata.js';
/**
 * The chart's read model (B1).
 *
 * ONE request per session range, carrying bars, indicator values, the daily context and the
 * coverage annotation together. Not one request per indicator: the chart has fourteen overlays
 * and a lower pane, and a request per overlay would make toggling one a network round trip,
 * which is the difference between an instrument that is pleasant to use for hours and one that
 * is not. The dataset it fetches is a few hundred kilobytes.
 *
 * Every indicator value is a DECIMAL STRING, exactly as `market.indicator_5m` holds it. The
 * chart does not recompute anything — that is the entire point of B0 — so what it displays in
 * the crosshair must be what the research pipeline reads, character for character. Parsing to a
 * float for plotting is fine; parsing to a float for DISPLAY is how the chart comes to show 31
 * while the model trains on 29.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
    .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), 'date must be a real date');
export const chartQuerySchema = z
    .object({
    symbol: z.string().regex(/^[A-Z][A-Z0-9.]{0,9}$/, 'symbol must be 1–10 upper-case chars'),
    from: isoDate,
    to: isoDate,
    feed: z.enum(REAL_FEED_NAMES).default('iex'),
    adjustment: z.enum(ADJUSTMENT_NAMES).default('split'),
    /** Daily bars of context ending at `to`. The strip shows about sixty. */
    dailyBars: z.coerce.number().int().min(0).max(250).default(60),
    /** Omit for whatever the table holds at the highest version for this scope. */
    indicatorVersion: z.coerce.number().int().min(1).optional(),
})
    .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['to'] })
    .refine((q) => Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`) <= 31 * 86_400_000, {
    message: 'range must not exceed 31 days — the chart loads one session range at a time',
    path: ['to'],
});
export const chartBarSchema = z.object({
    /** Bucket start, epoch seconds UTC — what lightweight-charts wants, without a parse step. */
    time: z.number().int(),
    open: z.string(),
    high: z.string(),
    low: z.string(),
    close: z.string(),
    volume: z.string(),
    /** 1-minute bars behind this bucket, 0–5. Below 5 the bucket is built from a gappy feed. */
    minuteCount: z.number().int(),
});
export const chartDailyBarSchema = z.object({
    time: z.number().int(),
    open: z.string(),
    high: z.string(),
    low: z.string(),
    close: z.string(),
});
export const chartSessionSchema = z.object({
    date: isoDate,
    /** Epoch seconds, so the chart can draw separators and the decision window without a parse. */
    open: z.number().int(),
    close: z.number().int(),
    earlyClose: z.boolean(),
    /** Regular-hours coverage, decimal string, or null when no coverage row exists. */
    coverage: z.string().nullable(),
    /**
     * True when re-fetching cannot improve this session — the feed has given everything it has.
     * A session that is merely stale looks identical on the chart and is not the same problem,
     * so the two are distinguished rather than both shown as "low coverage".
     */
    feedExhausted: z.boolean(),
});
/**
 * One indicator row, keyed by bucket time. Values are the stored decimal strings; the keys are
 * the registry's column names, so the crosshair readout and the research pipeline are reading
 * the same names for the same numbers.
 */
export const chartIndicatorRowSchema = z.object({
    time: z.number().int(),
    values: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
});
export const chartResponseSchema = z.object({
    symbol: z.string(),
    feed: z.enum(REAL_FEED_NAMES),
    adjustment: z.enum(ADJUSTMENT_NAMES),
    from: isoDate,
    to: isoDate,
    bars: z.array(chartBarSchema),
    daily: z.array(chartDailyBarSchema),
    sessions: z.array(chartSessionSchema),
    indicators: z.array(chartIndicatorRowSchema),
    /**
     * The version the indicator rows came from, or null when the range has none. Shown on the
     * chart: a chart that cannot say which definitions it is drawing is not reproducible.
     */
    indicatorVersion: z.number().int().nullable(),
});
/** The labelling gate. Below this a session is annotated on the chart and held back by B3. */
export const COVERAGE_GATE = 0.95;
/**
 * The decision window, in minutes from the session open: 09:45 to 15:45.
 *
 * Outside it there is no labelling. Before 09:45 the opening range has not closed and the
 * indicators that matter are still warming up; after 15:45 an exit cannot be modelled without
 * assuming something about the close. The chart marks it rather than letting the refusal be
 * discovered at the moment of committing a label.
 */
export const DECISION_WINDOW = { startMinute: 15, endMinute: 375 };
//# sourceMappingURL=chart.js.map